import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBateaux } from '@/hooks/useBateaux';
import { useMaintenances } from '@/hooks/useMaintenances';
import { useAssurances } from '@/hooks/useAssurances';
import { usePlacesPort } from '@/hooks/usePlacesPort';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import MainLayout from '@/components/layout/MainLayout';
import DocumentsDialog from '@/components/bateaux/DocumentsDialog';
import { Ship, ArrowLeft, Edit, FileText, Clock, Star, Trash2, Euro, Wrench, Shield, Anchor, Plus, Save, X, Edit2 } from 'lucide-react';
import FormulaireModificationBateau from '@/components/bateaux/FormulaireModificationBateau';
import { useDecryptedBoatFields } from '@/hooks/useDecryptedBoatFields';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

const openPortPage = async (portName: string, navigate: (p: string) => void) => {
  const { data } = await supabase
    .from('ports' as any)
    .select('id')
    .eq('nom', portName)
    .limit(1)
    .maybeSingle();
  if (data && (data as any).id) navigate(`/ports/${(data as any).id}`);
};

const BateauDetailPage = () => {
  const { t } = useTranslation('boats');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { bateaux, supprimerBateau } = useBateaux();
  const { maintenances } = useMaintenances();
  const { assurances, ajouterAssurance, modifierAssurance, getAssuranceParAnnee } = useAssurances(id);
  const { placesPort, ajouterPlacePort, modifierPlacePort, getPlacePortParAnnee } = usePlacesPort(id);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedCostYear, setSelectedCostYear] = useState<number>(new Date().getFullYear());
  const [editingInsurance, setEditingInsurance] = useState(false);
  const [insuranceCost, setInsuranceCost] = useState('');
  const [editingPortFee, setEditingPortFee] = useState(false);
  const [portFeeCost, setPortFeeCost] = useState('');



  const handleDelete = async () => {
    if (id) {
      await supprimerBateau(id);
      navigate('/mes-bateaux');
    }
  };
  
  const bateau = bateaux.find(b => b.id === id);

  const { decrypted, loading: sensitiveFieldsLoading } = useDecryptedBoatFields(id, {
    immatriculation: bateau?.immatriculation,
    mmsi: bateau?.mmsi,
    numero_hin: bateau?.numero_hin,
  });

  if (!bateau) {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <Ship className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {t("detail.notFoundTitle")}
            </h3>
            <p className="text-gray-500 mb-6">
              {t("detail.notFoundSubtitle")}
            </p>
            <Button
              onClick={() => navigate('/mes-bateaux')}
              className="bg-nautic-primary hover:bg-nautic-primary/90 text-white"
            >
              {t("detail.backToMyBoats")}
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/mes-bateaux')}
              className="flex items-center gap-2 text-nautic-primary hover:text-nautic-primary/90"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("detail.back")}
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-nautic-primary flex items-center gap-3">
                <Ship className="h-8 w-8" />
                {bateau.nom}
                {bateau.par_defaut && <Star className="h-6 w-6 text-yellow-500 fill-current" />}
              </h1>
              <p className="text-gray-600 mt-2">
                {bateau.nom_constructeur} {bateau.nom_modele} - {bateau.annee}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setDocumentsDialogOpen(true)}
            >
              <FileText className="h-4 w-4" />
              {t("detail.documents")}
            </Button>
            <Button
              className="bg-nautic-gold hover:bg-nautic-gold/90 text-nautic flex items-center gap-2"
              onClick={() => setShowEditForm(true)}
            >
              <Edit className="h-4 w-4" />
              {t("detail.edit")}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {t("detail.delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("detail.deleteConfirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("detail.deleteConfirmDescription")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("detail.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                    {t("detail.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Photo principale */}
        {bateau.photo_url && (
          <Card className="mb-8">
            <CardContent className="p-0">
              <div className="relative h-96 w-full overflow-hidden rounded-lg">
                <img 
                  src={bateau.photo_url} 
                  alt={bateau.nom} 
                  className="w-full h-full object-cover"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section Coûts */}
        {(() => {
          const currentYear = new Date().getFullYear();
          const availableYears = Array.from({ length: 7 }, (_, i) => currentYear + 1 - i);
          
          // Filtrer les maintenances du bateau pour l'année sélectionnée
          const maintenancesNavire = maintenances.filter(m => m.bateau_id === id);
          
          const filterByYear = (m: { date_echeance: string; prix?: number }) => {
            const year = new Date(m.date_echeance).getFullYear();
            return year === selectedCostYear && m.prix;
          };
          
          // Coûts par catégorie
          const coutEntretien = maintenancesNavire
            .filter(m => filterByYear(m) && ['Moteur', 'Coque', 'Voiles', 'Électricité', 'Plomberie'].includes(m.categorie))
            .reduce((total, m) => total + (m.prix || 0), 0);
          
          // Coût place au port pour l'année sélectionnée
          const placePortAnnee = getPlacePortParAnnee(selectedCostYear);
          const coutPlacePort = placePortAnnee?.cout || 0;
          
          // Coût assurance pour l'année sélectionnée
          const assuranceAnnee = getAssuranceParAnnee(selectedCostYear);
          const coutAssurance = assuranceAnnee?.cout || 0;
          
          const coutTotal = coutEntretien + coutPlacePort + coutAssurance;

          const handleSaveInsurance = async () => {
            const cost = parseFloat(insuranceCost) || 0;
            if (id) {
              if (assuranceAnnee) {
                await modifierAssurance(assuranceAnnee.id, { cout: cost });
              } else {
                await ajouterAssurance({
                  bateau_id: id,
                  annee: selectedCostYear,
                  cout: cost,
                  compagnie: null,
                  numero_contrat: null,
                  date_echeance: null,
                  notes: null
                });
              }
              setEditingInsurance(false);
              setInsuranceCost('');
            }
          };

          const handleSavePortFee = async () => {
            const cost = parseFloat(portFeeCost) || 0;
            if (id) {
              if (placePortAnnee) {
                await modifierPlacePort(placePortAnnee.id, { cout: cost });
              } else {
                await ajouterPlacePort({
                  bateau_id: id,
                  annee: selectedCostYear,
                  cout: cost,
                  nom_port: null,
                  notes: null,
                  user_id: ''
                });
              }
              setEditingPortFee(false);
              setPortFeeCost('');
            }
          };

          const handleEditInsurance = () => {
            setInsuranceCost(coutAssurance.toString());
            setEditingInsurance(true);
          };

          const handleCancelEditInsurance = () => {
            setEditingInsurance(false);
            setInsuranceCost('');
          };

          const handleEditPortFee = () => {
            setPortFeeCost(coutPlacePort.toString());
            setEditingPortFee(true);
          };

          const handleCancelEditPortFee = () => {
            setEditingPortFee(false);
            setPortFeeCost('');
          };

          return (
            <Card className="mb-8 border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("detail.totalCost")}</span>
                      <Select
                        value={selectedCostYear.toString()}
                        onValueChange={(value) => {
                          setSelectedCostYear(parseInt(value));
                          setEditingInsurance(false);
                          setInsuranceCost('');
                        }}
                      >
                        <SelectTrigger className="h-7 w-20 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableYears.map(year => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-3xl font-bold text-foreground">{coutTotal.toFixed(2)} €</p>
                    <p className="text-sm text-muted-foreground">{t("detail.totalCostCaption")}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Entretien */}
                  <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
                    <div className="p-2 bg-nautic-primary/10 rounded-lg">
                      <Wrench className="h-5 w-5 text-nautic-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("detail.maintenance")}</p>
                      <p className="text-xl font-semibold text-foreground">{coutEntretien.toFixed(2)} €</p>
                      <button 
                        onClick={() => navigate('/maintenance')}
                        className="text-xs text-nautic-primary hover:underline"
                      >
                        {t("detail.viewMaintenance")}
                      </button>
                    </div>
                  </div>
                  
                  {/* Place au port */}
                  <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
                    <div className="p-2 bg-nautic-primary/10 rounded-lg">
                      <Anchor className="h-5 w-5 text-nautic-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("detail.portFee")}</p>
                      {editingPortFee ? (
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            value={portFeeCost}
                            onChange={(e) => setPortFeeCost(e.target.value)}
                            placeholder="0.00"
                            className="h-8 w-24 text-sm"
                            min="0"
                            step="0.01"
                          />
                          <span className="text-sm">€</span>
                          <Button size="sm" variant="ghost" onClick={handleSavePortFee} className="h-7 w-7 p-0">
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={handleCancelEditPortFee} className="h-7 w-7 p-0">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-xl font-semibold text-foreground">{coutPlacePort.toFixed(2)} €</p>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={handleEditPortFee}
                            className="h-7 w-7 p-0"
                          >
                            {coutPlacePort > 0 ? <Edit2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Assurance */}
                  <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg">
                    <div className="p-2 bg-nautic-primary/10 rounded-lg">
                      <Shield className="h-5 w-5 text-nautic-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("detail.insurance")}</p>
                      {editingInsurance ? (
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            value={insuranceCost}
                            onChange={(e) => setInsuranceCost(e.target.value)}
                            placeholder="0.00"
                            className="h-8 w-24 text-sm"
                            min="0"
                            step="0.01"
                          />
                          <span className="text-sm">€</span>
                          <Button size="sm" variant="ghost" onClick={handleSaveInsurance} className="h-7 w-7 p-0">
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={handleCancelEditInsurance} className="h-7 w-7 p-0">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-xl font-semibold text-foreground">{coutAssurance.toFixed(2)} €</p>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={handleEditInsurance}
                            className="h-7 w-7 p-0"
                          >
                            {coutAssurance > 0 ? <Edit2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                          </Button>
                        </div>
                      )}
                      <button 
                        onClick={() => setDocumentsDialogOpen(true)}
                        className="text-xs text-nautic-primary hover:underline"
                      >
                        {t("detail.viewDocuments")}
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Grille d'informations - Nouveau design */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Informations générales */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-3">
                <div className="p-2 bg-nautic-primary/10 rounded-lg">
                  <Ship className="h-5 w-5 text-nautic-primary" />
                </div>
                {t("detail.generalInfo")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              {/* Constructeur & Modèle */}
              <div className="flex items-start gap-4 py-4 border-t border-border/50">
                <div className="p-2 bg-muted rounded-lg">
                  <Ship className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("detail.brandModel")}</p>
                  <p className="font-medium text-foreground">{bateau.nom_constructeur} • {bateau.nom_modele}</p>
                </div>
              </div>

              {/* Année */}
              <div className="flex items-start gap-4 py-4 border-t border-border/50">
                <div className="p-2 bg-muted rounded-lg">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("detail.year")}</p>
                  <p className="font-medium text-foreground">{bateau.annee}</p>
                </div>
              </div>

              {/* Dimensions */}
              <div className="flex items-start gap-4 py-4 border-t border-border/50">
                <div className="p-2 bg-muted rounded-lg">
                  <Ship className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("detail.dimensions")}</p>
                  <p className="font-medium text-foreground">
                    {bateau.longueur}m × {bateau.largeur}m
                    {bateau.tirant_eau && ` • ${t('detail.draftAbbrev')}: ${bateau.tirant_eau}m`}
                  </p>
                </div>
              </div>

              {/* Immatriculation */}
              <div className="flex items-start gap-4 py-4 border-t border-border/50">
                <div className="p-2 bg-muted rounded-lg">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("detail.registration")}</p>
                  <p className="font-medium text-foreground">{decrypted.immatriculation || (sensitiveFieldsLoading ? t('detail.decrypting') : '—')}</p>
                </div>
              </div>

              {/* Port d'attache */}
              {bateau.port_attache && (
                <button
                  type="button"
                  onClick={() => openPortPage(bateau.port_attache!, navigate)}
                  className="flex items-start gap-4 py-4 border-t border-border/50 w-full text-left hover:bg-muted/40 transition rounded-md px-1"
                >
                  <div className="p-2 bg-muted rounded-lg">
                    <Ship className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("detail.homePort")}</p>
                    <p className="font-medium text-primary hover:underline">{bateau.port_attache}</p>
                  </div>
                </button>
              )}

              {/* Pavillon */}
              {bateau.pavillon && (
                <div className="flex items-start gap-4 py-4 border-t border-border/50">
                  <div className="p-2 bg-muted rounded-lg">
                    <Star className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("detail.flag")}</p>
                    <p className="font-medium text-foreground">{bateau.pavillon}</p>
                  </div>
                </div>
              )}

              {/* Couleur */}
              {bateau.couleur && (
                <div className="flex items-start gap-4 py-4 border-t border-border/50">
                  <div className="p-2 bg-muted rounded-lg">
                    <Ship className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("detail.color")}</p>
                    <p className="font-medium text-foreground">{bateau.couleur}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Motorisation */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-3">
                <div className="p-2 bg-nautic-primary/10 rounded-lg">
                  <Clock className="h-5 w-5 text-nautic-primary" />
                </div>
                {t("detail.engineSection")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              {/* Moteur */}
              <div className="flex items-start gap-4 py-4 border-t border-border/50">
                <div className="p-2 bg-muted rounded-lg">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("detail.engine")}</p>
                  <p className="font-medium text-foreground">{bateau.moteur}</p>
                </div>
              </div>

              {/* Heures moteur */}
              <div className="flex items-start gap-4 py-4 border-t border-border/50">
                <div className="p-2 bg-muted rounded-lg">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("detail.engineHours")}</p>
                  <p className="font-medium text-foreground">{bateau.heures_moteur || 0} h</p>
                </div>
              </div>

              {/* Capacité carburant */}
              <div className="flex items-start gap-4 py-4 border-t border-border/50">
                <div className="p-2 bg-muted rounded-lg">
                  <Ship className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("detail.fuelCapacity")}</p>
                  <p className="font-medium text-foreground">{bateau.capacite_carburant} L</p>
                </div>
              </div>

              {/* Capacité eau */}
              <div className="flex items-start gap-4 py-4 border-t border-border/50">
                <div className="p-2 bg-muted rounded-lg">
                  <Ship className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t("detail.waterCapacity")}</p>
                  <p className="font-medium text-foreground">{bateau.capacite_eau} L</p>
                </div>
              </div>

              {/* Capacité eaux usées */}
              {bateau.capacite_eaux_usees && (
                <div className="flex items-start gap-4 py-4 border-t border-border/50">
                  <div className="p-2 bg-muted rounded-lg">
                    <Ship className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("detail.wasteWaterCapacity")}</p>
                    <p className="font-medium text-foreground">{bateau.capacite_eaux_usees} L</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Informations légales */}
        {(decrypted.immatriculation || bateau.port_attache || bateau.pavillon || decrypted.mmsi || decrypted.numero_hin || (sensitiveFieldsLoading && (bateau.immatriculation || bateau.mmsi || bateau.numero_hin))) && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-nautic-primary">{t("detail.legalInfo")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {sensitiveFieldsLoading && (bateau.immatriculation || bateau.mmsi || bateau.numero_hin) && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t("detail.identification")}</label>
                    <p className="font-semibold">{t("detail.decrypting")}</p>
                  </div>
                )}
                {decrypted.immatriculation && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t("detail.registration")}</label>
                    <p className="font-semibold">{decrypted.immatriculation}</p>
                  </div>
                )}
                {decrypted.mmsi && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t("detail.mmsi")}</label>
                    <p className="font-semibold">{decrypted.mmsi}</p>
                  </div>
                )}
                {decrypted.numero_hin && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t("detail.hinNumber")}</label>
                    <p className="font-semibold">{decrypted.numero_hin}</p>
                  </div>
                )}
                {bateau.port_attache && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t("detail.homePort")}</label>
                    <p>
                      <button
                        type="button"
                        onClick={() => openPortPage(bateau.port_attache!, navigate)}
                        className="font-semibold text-primary hover:underline"
                      >
                        {bateau.port_attache}
                      </button>
                    </p>
                  </div>
                )}
                {bateau.pavillon && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t("detail.flag")}</label>
                    <p className="font-semibold">{bateau.pavillon}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {bateau.note && (
          <Card>
            <CardHeader>
              <CardTitle className="text-nautic-primary">{t("detail.notes")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-wrap">{bateau.note}</p>
            </CardContent>
          </Card>
        )}

        {/* Dialog des documents */}
        {id && bateau && (
          <DocumentsDialog
            open={documentsDialogOpen}
            onClose={() => setDocumentsDialogOpen(false)}
            bateauId={id}
            bateauNom={bateau.nom}
          />
        )}


        {/* Dialog de modification */}
        <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("detail.editDialogTitle")}</DialogTitle>
            </DialogHeader>
            {bateau && (
              <FormulaireModificationBateau 
                bateau={bateau} 
                onClose={() => setShowEditForm(false)} 
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default BateauDetailPage;